import {
  createEffect,
  createMemo,
  on,
  createSignal,
  createSelector,
  //@ts-ignore
  For,
  onCleanup,
  onMount,
} from "solid-js";
import { IntrinsicTextNodeStyleProps, View, activeElement, hexColor, Text, Show } from "@lightningtv/solid";
import { Button, ButtonContainer, Column, Row } from "@lightningtv/solid-ui";
import { useLocation, useNavigate, useParams } from "@solidjs/router";
import seriesProvider from "../../api/providers/series";
import { createInfiniteScroll } from "../../components/pagination";
import seriesCategories from "../../api/providers/seriesCategories";
import { LazyUp } from "@lightningtv/solid/primitives";
import LoadingIndicator from "../loading/loading";
import { focusStore, playListPassword, playListUrl, playListUsername, setFocusStore } from "../../../state";
import { t } from "../../translations/configuration";
import { fetchWithCache } from "../movies/fetchwithcache";
import styles from "../../styles";
import ConfirmPin from "../../components/ConfirmPin";
import { getCORSSafeImageUrl, createCORSSafeImage } from "../../components/ImageCORSHandler";

const RowStyles: any = {
  display: "flex",
  justifyContent: "flexStart",
  width: 300,
  height: 300,
  gap: 26,
  y: 400,
} satisfies IntrinsicTextNodeStyleProps;

const Series = () => {
  const params = useParams();
  const navigate = useNavigate();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let myButton: any;
  let viewRef: any;
  let search: any;
  let firstRef: any;
  let settings: any;
  let focusTimeout: any;
  let cancelExit;
  let okExit;
  const location = useLocation();
  const [lastElement, setLastElement] = createSignal<any>(null);
  const [isNumberChanging, setIsNumberChanging] = createSignal(false);
  const [shouldWait, setShouldWait] = createSignal(false);
  const [heldCategory, setHeldCategory] = createSignal<{ id: number; callback: () => void } | null>(null);

  const [toggledCategoryIDs, setToggledCategoryIDs] = createSignal(
    JSON.parse(localStorage.getItem("seriesCategoryIDs") || "[]")
  );
  const [showPinDialog, setShowPinDialog] = createSignal(false);
  const [currentAction, setCurrentAction] = createSignal(null); // 'lock', 'unlock', or 'access'
  const [currentCategoryData, setCurrentCategoryData] = createSignal(null);
  const [pendingNavigation, setPendingNavigation] = createSignal(null);
  const [lastFocusedLazyUp, setLastFocusedLazyUp] = createSignal('first');
  const savedPin = localStorage.getItem('userPin');

  // Image cache for processed URLs
  const [imageCache, setImageCache] = createSignal<Map<string, string>>(new Map());

  // Function to get CORS-safe image URL with caching
  const getSafeImageUrl = async (originalUrl: string): Promise<string> => {
    if (!originalUrl) return "/assets/series-placeholder.png";

    // Check cache first
    const cached = imageCache().get(originalUrl);
    if (cached) {
      return cached;
    }

    try {
      const safeUrl = await getCORSSafeImageUrl(originalUrl, "/assets/series-placeholder.png");
      // Update cache
      const newCache = new Map(imageCache());
      newCache.set(originalUrl, safeUrl);
      setImageCache(newCache);
      return safeUrl;
    } catch (error) {
      console.error('Failed to get safe image URL:', error);
      return "/assets/series-placeholder.png";
    }
  };

  // Auto-patch problematic images on mount
  const patchProblematicImages = async () => {
    const problematicImages = document.querySelectorAll('img[src*="img5.steam.bz"], img[src*="steam.bz"]');
    for (const img of Array.from(problematicImages)) {
      const originalSrc = (img as HTMLImageElement).src;
      if (originalSrc && !originalSrc.startsWith('data:') && !originalSrc.startsWith('blob:')) {
        try {
          const safeSrc = await getSafeImageUrl(originalSrc);
          (img as HTMLImageElement).src = safeSrc;
          console.log('✅ Patched series image:', originalSrc.substring(0, 50) + '...');
        } catch (error) {
          console.error('❌ Failed to patch series image:', error);
          (img as HTMLImageElement).src = "/assets/series-placeholder.png";
        }
      }
    }
  };

  const providerCategory = createMemo(() => {
    return createInfiniteScroll(seriesCategories(params.filter || "all"));
  });

  const [number, setNumber] = createSignal<any>(null);

  const [showLoading, setShowLoading] = createSignal(true);
  const [showMessage, setShowMessage] = createSignal(false);

  const handleNumberChange = (newNumber: any) => {
    if (number() === newNumber) {
      return;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setIsNumberChanging(true);
    timeoutId = setTimeout(() => {
      setNumber(newNumber);
      setIsNumberChanging(false);
      timeoutId = null;
    }, 500);
  };

  const provider = createMemo(() => {
    return createInfiniteScroll(seriesProvider(params.filter || "all", number()));
  });

  createEffect(() => {
    localStorage.setItem("@emptyCategory", providerCategory().pages().length > 0 ? "false" : "true");

    const timeout = setTimeout(() => {
      setShowMessage(providerCategory().pages().length > 0 ? false : true);
      setShowLoading(false);
    }, 15000);

    onCleanup(() => clearTimeout(timeout));
  });

  const handlePinConfirm = (pin) => {
    const action = currentAction();
    const categoryData = currentCategoryData();
    const navigation = pendingNavigation();

    if (!categoryData) return;

    const savedPin = localStorage.getItem("userPin");

    if (pin === savedPin) {
      switch (action) {
        case 'lock':
          lockCategory(categoryData.id);
          break;
        case 'unlock':
          unlockCategory(categoryData.id);
          break;
        case 'access':
          if (navigation) {
            navigate(navigation.url);
            setFocusStore("moviesDetail", navigation.index);
          }
          break;
      }
    } else {
      console.log("Incorrect PIN - Access denied");
    }

    setShowPinDialog(false);
    setCurrentAction(null);
    setCurrentCategoryData(null);
    setPendingNavigation(null);
  };

  const lockCategory = (categoryID) => {
    const savedIDs = [...toggledCategoryIDs()];
    if (!savedIDs.includes(categoryID)) {
      savedIDs.push(categoryID);
      localStorage.setItem("seriesCategoryIDs", JSON.stringify(savedIDs));
      setToggledCategoryIDs(savedIDs);
      console.log("Category locked:", savedIDs);
    }
  };

  const unlockCategory = (categoryID) => {
    const savedIDs = [...toggledCategoryIDs()];
    const indexInArray = savedIDs.indexOf(categoryID);
    if (indexInArray !== -1) {
      savedIDs.splice(indexInArray, 1);
      localStorage.setItem("seriesCategoryIDs", JSON.stringify(savedIDs));
      setToggledCategoryIDs(savedIDs);
      console.log("Category unlocked:", savedIDs);
    }
  };

  const handleMovieAccess = (movieData, movieIndex, categoryId) => {
    const isLocked = toggledCategoryIDs().includes(categoryId);

    if (isLocked) {
      setCurrentCategoryData({ id: categoryId });
      setCurrentAction('access');
      setPendingNavigation({
        url: `/serieDetails/series/${movieData.series_id}/${movieData.category_id}`,
        index: movieIndex
      });
      setShowPinDialog(true);
    } else {
      navigate(`/serieDetails/series/${movieData.series_id}/${movieData.category_id}`);
      setFocusStore("moviesDetail", movieIndex);
    }
  };

  const [isEnterProcessing, setIsEnterProcessing] = createSignal(false);
  const [isDialogOpenExit, setIsDialogOpenExit] = createSignal<boolean>(false);

  let enterCallCount = 0;
  let enterCallTimer = null;

  const handleRepeatedEnter = (categoryID, toggleCategoryCallback) => {
    enterCallCount++;

    if (enterCallTimer) {
      clearTimeout(enterCallTimer);
    }

    if (enterCallCount === 1) {
      console.log('Started counting Enter presses...');
    }

    enterCallTimer = setTimeout(() => {
      console.log(`Enter was called ${enterCallCount} times, then stopped`);
      enterCallCount = 0;
    }, 100);

    if (enterCallCount >= 10 && !isEnterProcessing()) {
      console.log('Enter "held" detected! Executing logic...');
      setIsEnterProcessing(true);
      enterCallCount = 0;

      if (enterCallTimer) {
        clearTimeout(enterCallTimer);
      }

      if (localStorage.getItem('userPin') === null) {
        console.log('there is no pin set');
        setTimeout(() => {
          cancelExit.setFocus()
        }, 600)
        setIsDialogOpenExit(true)
      } else {
        toggleCategoryCallback();
      }

      setTimeout(() => {
        setIsEnterProcessing(false);
      }, 1000);
    }
  };

  const enterHoldFunc = (categoryID, toggleCategoryCallback) => {
    if (localStorage.getItem('userPin') === null) {
      console.log('there is no pin set');
      setTimeout(() => {
        cancelExit.setFocus()
      }, 600)
      setIsDialogOpenExit(true)
    } else {
      toggleCategoryCallback();
    }
  }

  let holdTimer = null;
  let enterHeld = false;

  const handleKeyDown = (e) => {
    const held = heldCategory();
    if (!held) return;

    if ((e.key === "Enter" || e.keyCode === 13) && !holdTimer) {
      holdTimer = setTimeout(() => {
        enterHeld = true;
        enterHoldFunc(held.id, held.callback);
      }, 600);
    }
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      enterHeld = false;
    }
  };

  onMount(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Auto-patch images after mount
    setTimeout(patchProblematicImages, 1000);

    // Set up periodic image patching for dynamically loaded content
    const patchInterval = setInterval(patchProblematicImages, 3000);

    onCleanup(() => {
      clearInterval(patchInterval);
    });
  });

  onCleanup(() => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
  });

  // CORS-safe icon component
  function CORSSafeIcon(props: { src: string; width: number; height: number; x: number; y: number }) {
    const { imageUrl, loadImage } = createCORSSafeImage();

    createEffect(() => {
      if (props.src) {
        loadImage(props.src, "/assets/icon-placeholder.png");
      }
    });

    return (
      <View
        src={imageUrl()}
        width={props.width}
        height={props.height}
        x={props.x}
        y={props.y}
      />
    );
  }

  function Thumbnail(props: any) {
    const newData = props;
    const currentCategoryID = props.categoryID;
    const isToggled = () => toggledCategoryIDs().includes(currentCategoryID);

    const toggleCategory = () => {
      const isCurrentlyLocked = isToggled();
      const categoryData = {
        id: currentCategoryID,
        name: newData.category_name
      };

      setCurrentCategoryData(categoryData);
      setCurrentAction(isCurrentlyLocked ? 'unlock' : 'lock');
      setShowPinDialog(true);
    };

    return (
      <View
        ref={myButton}
        style={{
          borderRadius: 8,
          color: newData.index === lastElement() ? hexColor("#128DD3") : hexColor("#484e5599"),
          focus: { color: hexColor("#128DD3"), textColor: hexColor("#ffffff") },
          transition: {
            scale: { duration: 250, easing: "ease-in-out" },
          },
          width: 500,
          height: 120,
          justifyContent: "center",
          alignItems: "center",
        }}
        onFocus={() => {
          handleNumberChange(newData.categoryID);
          setFocusStore("seriesIndex", newData.index);
          setFocusStore("seriesID", newData.categoryID);

          setHeldCategory({
            id: newData.categoryID,
            callback: () => {
              const isCurrentlyLocked = isToggled();
              const categoryData = {
                id: newData.categoryID,
                name: newData.category_name,
              };
              setCurrentCategoryData(categoryData);
              setCurrentAction(isCurrentlyLocked ? "unlock" : "lock");
              setShowPinDialog(true);
            },
          });
        }}
        onUp={() => {
          if (shouldWait()) {
            return true;
          } else {
            //@ts-ignore
            setFocusStore("seriesDetail", 0);
          }
        }}
        onEnterHold={() => {
          enterHoldFunc(currentCategoryID, toggleCategory);
        }}
        onDown={() => {
          if (shouldWait()) {
            return true;
          } else {
            //@ts-ignore
            setFocusStore("seriesDetail", 0);
          }
        }}
      >
        <Text fontSize={26} x={23} y={45}>
          {newData.category_name}
        </Text>
        <Show when={savedPin}>
          <View
            src={
              isToggled()
                ? "https://i.ibb.co/jPvhs2HJ/lock.png"
                : "https://i.ibb.co/N2Ym8McH/unlock.png"
            }
            width={30}
            height={30}
            x={420}
            y={50}
          />
        </Show>
      </View>
    );
  }

  // Enhanced ItemMovie component with CORS-safe images
  function ItemMovie(props: any) {
    const newData = props;
    const rating = newData?.data?.rating_5based ?? 0;

    // Use CORS-safe image handler for the series poster
    const { imageUrl, isLoading, loadImage, hasError } = createCORSSafeImage();

    // Load the series poster image
    createEffect(() => {
      if (newData.src) {
        loadImage(newData.src, "/assets/series-placeholder.png");
      }
    });

    const handleEnter = () => {
      const currentCategoryId = number();
      handleMovieAccess(newData.data, newData.index, currentCategoryId);
    };

    return (
      <View
        ref={myButton}
        style={{
          borderRadius: 8,
          border: { width: 0, color: hexColor("#45617a") },
          color: hexColor("#484e55"),
          focus: { color: hexColor("#128DD3"), textColor: hexColor("#ffffff") },
          transition: {
            scale: { duration: 250, easing: "ease-in-out" },
          },
          height: 250,
          width: 1300,
          justifyContent: "flexStart",
          alignItems: "flexStart",
        }}
        //@ts-ignore
        states={"active"}
        onFocus={() => {
          localStorage.removeItem("seriesDetail");
        }}
        onEnter={handleEnter}
      >
        {/* Series poster with loading state */}
        <View x={7} y={7} style={{ width: 150, height: 235, borderRadius: 10, position: 'relative' }}>
          <Show when={isLoading()}>
            <View style={{
              width: 150,
              height: 235,
              borderRadius: 10,
              backgroundColor: '#333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Text style={{ color: '#666', fontSize: 12 }}>Loading...</Text>
            </View>
          </Show>
          <Show when={!isLoading()}>
            <View
              src={imageUrl()}
              style={{
                width: 150,
                height: 235,
                borderRadius: 10,
                display: isLoading() ? 'none' : 'block'
              }}
            />
          </Show>
        </View>

        <View x={20}>
          <Text x={160} y={20} style={{ fontSize: 36 }}>
            {newData.data.name}
          </Text>

          <View x={10} y={70}>
            {Array.from({ length: 5 }).map((_, index) => {
              const fullStars = Math.floor(rating);
              const hasHalfStar = rating % 1 >= 0.5;
              const starXPosition = 150 + index * 40;

              let starSrc = "https://i.ibb.co/1KBsPWh/star-2.png"; // Empty star
              if (index < fullStars) {
                starSrc = "https://i.ibb.co/4gK6MnK/star-1.png"; // Full star
              } else if (index === fullStars && hasHalfStar) {
                starSrc = "https://i.ibb.co/9V7LVwc/rating.png"; // Half star
              }

              return (
                <View
                  key={index}
                  src={starSrc}
                  x={starXPosition}
                  width={30}
                  height={30}
                />
              );
            })}
          </View>
          <Text x={160} y={120} style={{ fontSize: 32 }}>
            {newData.data.rating_5based}/5
          </Text>
        </View>
      </View>
    );
  }

  // CORS-safe star component
  function CORSSafeStar(props: { src: string; x: number; width: number; height: number }) {
    const { imageUrl, loadImage } = createCORSSafeImage();

    createEffect(() => {
      if (props.src) {
        loadImage(props.src, "/assets/star-placeholder.png");
      }
    });

    return (
      <View
        x={props.x}
        width={props.width}
        height={props.height}
        src={imageUrl()}
      />
    );
  }

  createEffect(() => {
    const enterID = focusStore.seriesID;

    if (enterID !== null) {
      handleNumberChange(enterID);
    }
  });

  onMount(() => {
    if (focusStore.seriesIndex === null || focusStore.seriesIndex === undefined) {
      //@ts-ignore
      setFocusStore("seriesIndex", 0);
      return;
    }

    const index = focusStore.seriesIndex;
    setShouldWait(true);

    if (providerCategory() && location.pathname.startsWith("/series")) {
      focusTimeout = setTimeout(() => {
        viewRef?.setFocus();
        setShouldWait(false);
      }, 1500);
    }
  });

  onCleanup(() => {
    clearTimeout(focusTimeout);
  });

  return (
    <Show when={showPinDialog()} fallback={
      <View
        color={hexColor("#182027")}
        onEscape={() => {
          if (!shouldWait()) {
            navigate("/home");

            setFocusStore("seriesIndex", null);
            setFocusStore("seriesDetail", null);
            setFocusStore("seriesID", null);
            clearTimeout(focusTimeout);
          }
        }}
      >
        <View src={"https://i.ibb.co/Lzk5hD4Y/Maskgroup.png"} >

          <Show when={isDialogOpenExit()}>
            <View x={500} y={350} width={800} height={300} color={hexColor("#ffffff")} borderRadius={20}>
              <Text x={50} y={30} style={{ fontSize: 24, color: hexColor("#000000"), fontWeight: "bold" }}>
                PIN Not Set
              </Text>
              <Text x={50} y={100} style={{ fontSize: 24, color: hexColor("#000000") }}>
                Would you like to set up your PIN now?
              </Text>

              <View
                ref={el => {
                  cancelExit = el;
                }}
                x={430}
                y={220}
                style={{
                  borderRadius: 15,
                  color: hexColor("#C1C3C1"),
                  focus: { color: hexColor("#128DD3") },
                  transition: {
                    scale: { duration: 250, easing: "ease-in-out" },
                  },
                  height: 45,
                  width: 150,
                }}
                onEnter={() => {
                  firstRef.setFocus();
                  setIsDialogOpenExit(false)
                }}
                textColor={hexColor("#000000")}
                onRight={() => okExit.setFocus()}
              >
                <Text x={40} y={15} style={{ fontSize: 18, color: hexColor("#000000") }}>
                  {t("cancelBig")}
                </Text>
              </View>
              <View
                ref={el => {
                  okExit = el;
                }}
                x={600}
                y={220}
                style={{
                  borderRadius: 15,
                  color: hexColor("#C1C3C1"),
                  focus: { color: hexColor("#128DD3") },
                  transition: {
                    scale: { duration: 250, easing: "ease-in-out" },
                  },
                  height: 45,
                  width: 150,
                }}
                onLeft={() => cancelExit.setFocus()}
                onEnter={() => {
                  console.log('send user to settings');
                  navigate("/userinfo?fromPIN=true")
                }}
                textColor={hexColor("#000000")}
              >
                <Text x={60} y={15} style={{ fontSize: 18, color: hexColor("#000000") }}>
                  OK
                </Text>
              </View>
            </View>
          </Show>

          <View src="https://i.ibb.co/ZVmDwNg/logo.png" x={800} y={50} width={270} height={82} />

          <View x={50} y={50}>
            <View src="https://i.ibb.co/LX13T2JW/Group-Live-Tv.png" width={102} height={102} />
            <Text x={120} y={33} fontSize={34} fontWeight={"bold"}  >Series</Text>
          </View>

          <View x={1600} y={50}>
            <View
              ref={search}
              onEnter={() => {
                navigate("/searchseries");
                localStorage.removeItem("focusedIndex");
              }}
              onDown={() => {
                if (location.pathname.startsWith("/series")) {
                  if (lastFocusedLazyUp() === 'second') {
                    viewRef.setFocus();
                  } else {
                    firstRef.setFocus();
                  }
                }
              }}
              onRight={() => {
                settings.setFocus();
              }}
              style={styles.searchButton} >
              <View x={28} y={28} src="https://i.ibb.co/nNhBcw8M/search.png" width={42} height={42} />
            </View>

            <View
              onLeft={() => {
                search.setFocus()
              }}
              onDown={() => {
                if (location.pathname.startsWith("/series")) {
                  if (lastFocusedLazyUp() === 'second') {
                    viewRef.setFocus();
                  } else {
                    firstRef.setFocus();
                  }
                }
              }}
              onEnter={() => {
                navigate("/userInfo");
              }}
              ref={settings} x={120} style={{ width: 100, height: 100 }} style={styles.searchButton}>
              <View x={28} y={28} src="https://i.ibb.co/Jjp8bmn4/setting.png" width={42} height={42} />
            </View>
          </View>

          <View y={150}>
            <Show
              when={providerCategory().pages() && providerCategory().pages().length > 0}
              fallback={
                <View x={750} y={500}>
                  <Text fontSize={30} color={hexColor("#7d7e82")}>
                    {t("noSeries")}
                  </Text>
                </View>
              }
            >
              <View clipping y={30}>
                {/* @ts-ignore */}
                <LazyUp
                  onRight={() => {
                    if (provider().pages() && provider().pages()?.length > 0 && !isNumberChanging()) {
                      viewRef.setFocus();
                      setShowLoading(false);
                    } else {
                      console.log("akoma jo");
                      setShowLoading(true);
                    }
                  }}
                  onBlur={() => {
                    setHeldCategory(null)
                  }}
                  onFocus={() => setLastFocusedLazyUp('first')}
                  onUp={() => search.setFocus()}
                  x={80}
                  y={10}
                  gap={10}
                  style={RowStyles}
                  onSelectedChanged={setLastElement}
                  autofocus={true}
                  component={Column}
                  ref={firstRef}
                  scroll="always"
                  upCount={15}
                  selected={focusStore.seriesIndex === null ? 0 : focusStore.seriesIndex}
                  each={providerCategory().pages()}
                >
                  {(item: any, i: number) => <Thumbnail {...item} index={i} />}
                </LazyUp>

                <View x={570} y={10} clipping>
                  <Show
                    when={provider().pages() && provider().pages().length > 0 && !isNumberChanging()}
                    fallback={
                      <View x={500} y={330} width={100} height={100}>
                        {showLoading() && <LoadingIndicator isCategoriesIndicator={true} />}
                        {showMessage() && (
                          <Text y={50} style={{ color: hexColor("#7d7e82"), fontSize: 22, width: 10 }}>
                            {t("categoryEmpty")}
                          </Text>
                        )}
                      </View>
                    }
                  >
                    {/* @ts-ignore */}
                    <LazyUp
                      x={20}
                      onLeft={() => firstRef.setFocus()}
                      component={Column}
                      ref={viewRef}
                      onFocus={() => setLastFocusedLazyUp('second')}
                      onUp={() => search.setFocus()}
                      scroll="always"
                      upCount={6}
                      gap={8}
                      selected={
                        focusStore.seriesDetail === null
                          ? 0
                          : (() => {
                            const index = focusStore.seriesDetail;
                            if (index !== null) {
                              setTimeout(() => { }, 300);
                              return index;
                            }
                          })()
                      }
                      each={provider().pages()}
                    >
                      {(item: any, i: number) => <ItemMovie {...item} index={i} />}
                    </LazyUp>
                  </Show>
                </View>
              </View>
            </Show>
          </View>
        </View>
      </View>}>
      <View onEscape={() => {
        if (showPinDialog()) {
          setShowPinDialog(false)
          console.log('hyri ketu');
        }
      }}>
        <ConfirmPin onPinSet={handlePinConfirm} />
      </View>
    </Show>
  );
};

export default Series;